// ZERCODE CHANGE [STD-FE]: Berry's demo profile menu (greeting card, profile
// search, DND/notification switches, UpgradePlanCard, social profile) removed.
// The menu now shows the REAL signed-in user (name + e-mail via hooks/useAuth,
// which refreshes from Identity's `me` endpoint), links to the tenant branding
// settings and performs a working logout (Identity logout -> session cleared
// -> /login).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third party
import { FormattedMessage } from 'react-intl';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { useMyAvatarUrl } from '@zercode/auth';

import useAuth from 'hooks/useAuth';

// project imports
import { authApi, loginUrl } from '../../../../../lib/zercode';

// assets
import User1 from 'assets/images/users/user-round.svg';
import { IconLogout, IconPalette, IconSettings, IconUser } from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';

// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const {
    state: { borderRadius }
  } = useConfig();
  const navigate = useNavigate();

  const { logout, user } = useAuth();
  // AVATAR-UPLOAD (mig 309): the picture is authenticated bytes, so it is fetched and wrapped in an
  // object URL here rather than passed down as a src. Falls back to the seed image when there is none.
  const avatarUrl = useMyAvatarUrl(authApi, user?.hasAvatar);
  const [open, setOpen] = useState(false);

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);

  const handleLogout = async () => {
    try {
      // Identity logout; @zercode/auth clears the session even if the call fails
      await logout();
    } catch (err) {
      console.error(err);
    } finally {
      // FE-6: no in-app login page — return to the dedicated login app cross-origin.
      window.location.assign(loginUrl);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleNavigate = (event: React.MouseEvent<HTMLDivElement>, route: string) => {
    handleClose(event);
    navigate(route);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        slotProps={{ label: { sx: { lineHeight: 0 } } }}
        sx={{ ml: 2, height: '48px', alignItems: 'center', borderRadius: '27px' }}
        icon={
          <Avatar
            src={avatarUrl || User1}
            alt={user?.name ?? 'user'}
            sx={{ typography: 'mediumAvatar', margin: '8px 0 8px 8px !important', cursor: 'pointer' }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={<IconSettings stroke={1.5} size="24px" />}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
        aria-label="user-account"
      />
      {/* ZERCODE CHANGE [STD-FE] — HEADER-MENU-PHONE: the panel used to be clipped by the screen edge.
          The list below carries `minWidth: 300`, which with the card's 2×16px padding makes a 332px panel:
          wider than a 320px phone, and exactly the width of a 360/375px one, so it sat flush against the
          right edge with no gutter and lost 12px off the end on the narrowest common Android.

          ⚠ THIS IS NOT THE CONTAINING-BLOCK BUG FROM THE PUBLIC SITE, and it was worth checking rather
          than assuming: `disablePortal` does put this popper inside a `position: fixed` AppBar, but the
          AppBar carries no `backdrop-filter`/`transform`/`filter`, so it is NOT a containing block and the
          popper (measured `position: fixed`) still resolves against the viewport. Only the WIDTH was wrong.

          Two halves, and both are needed: the cap stops the panel being wider than the screen, and
          `preventOverflow` keeps a real 16px gutter instead of letting it sit flush. Measured at 320px:
          332px clipped by 12 → 288px with 16px each side. At 1280px it is 332px, exactly as before. */}
      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ maxWidth: 'calc(100vw - 32px)' }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 16
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                    <Box sx={{ p: 2, pb: 1 }}>
                      {/* signed-in user identity from Identity `me` (via hooks/useAuth) */}
                      <Stack>
                        <Typography variant="h4">{user?.name ?? '—'}</Typography>
                        <Typography variant="subtitle2">{user?.email}</Typography>
                      </Stack>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 2, pt: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          // ⚠ A FLOOR ONLY WHERE THERE IS ROOM FOR ONE. 300px is right on a laptop and
                          // impossible on a 320px phone, where it is wider than the screen once the card's
                          // padding is added. `xs: 0` lets the panel be as narrow as the viewport allows;
                          // the cap on the Popper above is what stops it going wider.
                          minWidth: { xs: 0, sm: 300 },
                          borderRadius: `${borderRadius}px`,
                          '& .MuiListItemButton-root': { mt: 0.5 }
                        }}
                      >
                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => handleNavigate(event, '/profile')}
                        >
                          <ListItemIcon>
                            <IconUser stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="account-menu" defaultMessage="Account Center" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          onClick={(event: React.MouseEvent<HTMLDivElement>) => handleNavigate(event, '/settings/branding')}
                        >
                          <ListItemIcon>
                            <IconPalette stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="branding-settings-title" defaultMessage="Branding" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        <ListItemButton sx={{ borderRadius: `${borderRadius}px` }} onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="logout" defaultMessage="Logout" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
